import { getApiBaseUrl } from '../lib/getApiUrl';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DocumentationArticle,
  DocumentationArticleListItem,
  DocumentationCategoryInfo,
  DocumentationTip,
  DocumentationSearchResult,
} from '../types/documentation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

// API functions
export const documentationApi = {
  // Get all categories with counts
  async getCategories(): Promise<DocumentationCategoryInfo[]> {
    const response = await fetch(`${API_BASE_URL}/docs/categories`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch categories');
    const data = await response.json();
    return data.data;
  },

  // Get articles list with optional filters
  async getArticles(params?: {
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ articles: DocumentationArticleListItem[]; total: number }> {
    const urlParams = new URLSearchParams();
    if (params?.category) urlParams.append('category', params.category);
    if (params?.search) urlParams.append('search', params.search);
    if (params?.limit) urlParams.append('limit', String(params.limit));
    if (params?.offset) urlParams.append('offset', String(params.offset));

    const response = await fetch(`${API_BASE_URL}/docs/articles?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch articles');
    const data = await response.json();
    return { articles: data.data, total: data.meta.total };
  },

  // Get single article by slug
  async getArticle(slug: string): Promise<DocumentationArticle> {
    const response = await fetch(`${API_BASE_URL}/docs/articles/${slug}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch article');
    const data = await response.json();
    return data.data;
  },

  // Get articles for a specific page route
  async getArticlesByRoute(route: string): Promise<DocumentationArticleListItem[]> {
    // Remove leading slash for the API call
    const cleanRoute = route.startsWith('/') ? route.slice(1) : route;
    const response = await fetch(`${API_BASE_URL}/docs/articles/by-route/${cleanRoute}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch articles for route');
    const data = await response.json();
    return data.data;
  },

  // Search documentation
  async search(query: string, category?: string): Promise<DocumentationSearchResult[]> {
    const urlParams = new URLSearchParams({ q: query });
    if (category) urlParams.append('category', category);

    const response = await fetch(`${API_BASE_URL}/docs/search?${urlParams}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to search documentation');
    const data = await response.json();
    return data.data;
  },

  // Get tips for a page
  async getTips(pageRoute: string): Promise<DocumentationTip[]> {
    // Remove leading slash for the API call
    const cleanRoute = pageRoute.startsWith('/') ? pageRoute.slice(1) : pageRoute;
    const response = await fetch(`${API_BASE_URL}/docs/tips/${cleanRoute}`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to fetch tips');
    const data = await response.json();
    return data.data;
  },

  // Admin: Create article
  async createArticle(article: Partial<DocumentationArticle>): Promise<DocumentationArticle> {
    const response = await fetch(`${API_BASE_URL}/docs/articles`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(article),
    });
    if (!response.ok) throw new Error('Failed to create article');
    const data = await response.json();
    return data.data;
  },

  // Admin: Update article
  async updateArticle(id: string, article: Partial<DocumentationArticle>): Promise<DocumentationArticle> {
    const response = await fetch(`${API_BASE_URL}/docs/articles/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(article),
    });
    if (!response.ok) throw new Error('Failed to update article');
    const data = await response.json();
    return data.data;
  },

  // Admin: Delete article
  async deleteArticle(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/docs/articles/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) throw new Error('Failed to delete article');
  },
};

// React Query hooks

// Hook to get categories
export function useDocumentationCategories() {
  return useQuery({
    queryKey: ['documentation', 'categories'],
    queryFn: documentationApi.getCategories,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get articles list
export function useDocumentationArticles(params?: {
  category?: string;
  search?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['documentation', 'articles', params],
    queryFn: () => documentationApi.getArticles(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook to get single article
export function useDocumentationArticle(slug: string | undefined) {
  return useQuery({
    queryKey: ['documentation', 'article', slug],
    queryFn: () => documentationApi.getArticle(slug!),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to get articles for current page
export function usePageDocumentation(pageRoute: string) {
  return useQuery({
    queryKey: ['documentation', 'byRoute', pageRoute],
    queryFn: () => documentationApi.getArticlesByRoute(pageRoute),
    enabled: !!pageRoute,
    staleTime: 5 * 60 * 1000,
  });
}

// Hook to search documentation
export function useDocumentationSearch(query: string, category?: string) {
  return useQuery({
    queryKey: ['documentation', 'search', query, category],
    queryFn: () => documentationApi.search(query, category),
    enabled: query.length >= 2,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

// Hook to get page tips
export function usePageTips(pageRoute: string) {
  return useQuery({
    queryKey: ['documentation', 'tips', pageRoute],
    queryFn: () => documentationApi.getTips(pageRoute),
    enabled: !!pageRoute,
    staleTime: 5 * 60 * 1000,
  });
}

// Admin hooks
export function useCreateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: documentationApi.createArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation'] });
    },
  });
}

export function useUpdateArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, article }: { id: string; article: Partial<DocumentationArticle> }) =>
      documentationApi.updateArticle(id, article),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation'] });
    },
  });
}

export function useDeleteArticle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: documentationApi.deleteArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documentation'] });
    },
  });
}
